import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';
import { X, Bell, BellOff } from 'lucide-react';

const BACKEND_URL = getBackendUrl();

// Helper to get token
const getToken = () => storage.getItem('token');

// Request browser notification permission
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }
  
  return Notification.permission;
};

// Send browser notification
const sendBrowserNotification = (title, body, icon = '📊') => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'scheduler-notification',
      requireInteraction: true, // Keep notification until user interacts
    });
    
    // Focus window when notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    
    return notification;
  }
  return null;
};

/**
 * Scheduler Status Component
 * Shows real-time status of all scheduled jobs and allows manual triggering
 * 
 * UNIFIED: Uses the same enrichment_service as StreamMonitor for consistency
 */
const SchedulerStatus = () => {
  const [schedulerData, setSchedulerData] = useState(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [contentStatus, setContentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  const [enrichmentPolling, setEnrichmentPolling] = useState(false);
  const pollingInterval = useRef(null);
  
  // Persistent notifications state
  const [notifications, setNotifications] = useState([]);
  const prevEnrichmentStatus = useRef(null);
  
  // Browser notification permission state
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window 
      ? Notification.permission 
      : 'unsupported'
  );

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't auto-request, wait for user action
    }
  }, []);

  const toggleBrowserNotifications = async () => {
    if (notificationPermission === 'granted') {
      // Can't revoke, just inform user
      alert('Για να απενεργοποιήσετε τις ειδοποιήσεις, αλλάξτε τις ρυθμίσεις του browser σας.');
      return;
    }
    
    const permission = await requestNotificationPermission();
    setNotificationPermission(permission);
    
    if (permission === 'granted') {
      sendBrowserNotification(
        '🔔 Ειδοποιήσεις Ενεργές',
        'Θα λαμβάνετε ειδοποιήσεις όταν ολοκληρώνονται τα jobs.'
      );
    }
  };

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/scheduler-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch scheduler status');
      
      const data = await response.json();
      setSchedulerData(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching scheduler status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch content generation status
  const fetchContentStatus = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/dashboard/system-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContentStatus(data.content_generation || null);
      }
    } catch (err) {
      console.error('Error fetching content status:', err);
    }
  }, []);

  const fetchEnrichmentStatus = useCallback(async () => {
    try {
      const token = getToken();
      // Use the SAME endpoint as StreamMonitor for consistency
      const response = await fetch(`${BACKEND_URL}/api/stream-monitor/enrich-release-years/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check for status change to show notification
        const prevStatus = prevEnrichmentStatus.current?.status;
        if (prevStatus === 'running' && data.status !== 'running') {
          // Job just finished
          const updated = data.updated || prevEnrichmentStatus.current?.updated || 0;
          const failed = data.failed || prevEnrichmentStatus.current?.failed || 0;
          const total = data.total || prevEnrichmentStatus.current?.total || 0;
          
          if (data.status === 'complete') {
            addNotification('success', 
              `✅ Ολοκληρώθηκε η ενημέρωση ετών!\n` +
              `   • Βρέθηκαν: ${updated} έτη\n` +
              `   • Δεν βρέθηκαν: ${failed}\n` +
              `   • Σύνολο: ${total} τραγούδια`
            );
          } else if (data.status === 'stopped') {
            addNotification('warning', 
              `⏹️ Σταμάτησε η ενημέρωση ετών\n` +
              `   • Βρέθηκαν: ${updated} έτη\n` +
              `   • Δεν βρέθηκαν: ${failed}`
            );
          } else if (data.status === 'error') {
            addNotification('error', `❌ Σφάλμα: ${data.message || 'Άγνωστο σφάλμα'}`);
          }
        }
        
        prevEnrichmentStatus.current = data;
        setEnrichmentStatus(data);
        
        // Auto-start polling if job is running
        if (data.status === 'running' && !enrichmentPolling) {
          setEnrichmentPolling(true);
        }
        // Stop polling when complete
        if (data.status !== 'running' && enrichmentPolling) {
          setEnrichmentPolling(false);
        }
      }
    } catch (err) {
      console.error('Error fetching enrichment status:', err);
    }
  }, [enrichmentPolling]);

  // Add notification helper - also sends browser notification if permitted
  const addNotification = (type, message, sendBrowser = true) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message, timestamp: new Date() }]);
    
    // Send browser notification if enabled
    if (sendBrowser && notificationPermission === 'granted') {
      const title = type === 'success' ? '✅ Job Ολοκληρώθηκε' 
                  : type === 'warning' ? '⚠️ Job Σταμάτησε'
                  : '❌ Σφάλμα Job';
      sendBrowserNotification(title, message);
    }
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    fetchSchedulerStatus();
    fetchEnrichmentStatus();
    fetchContentStatus();
    
    // Refresh scheduler every 30 seconds
    const interval = setInterval(() => {
      fetchSchedulerStatus();
      fetchContentStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchSchedulerStatus, fetchEnrichmentStatus, fetchContentStatus]);

  // Fast polling when enrichment is running (every 3 seconds)
  useEffect(() => {
    if (enrichmentPolling) {
      pollingInterval.current = setInterval(() => {
        fetchEnrichmentStatus();
      }, 3000);
    } else if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [enrichmentPolling, fetchEnrichmentStatus]);

  const runJob = async (jobType) => {
    setRunningJob(jobType);
    
    // UNIFIED: Use the SAME endpoints as StreamMonitor
    const endpoint = jobType === 'enrichment' 
      ? '/api/stream-monitor/enrich-release-years/start'  // Same as StreamMonitor
      : '/api/admin/system/run-duplicate-cleanup-now?dry_run=false';
    
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (jobType === 'enrichment') {
        // Start polling for progress
        if (result.success !== false) {
          setEnrichmentPolling(true);
          fetchEnrichmentStatus();
        }
        // Notifications will show when job completes via polling
      } else {
        // Show persistent notification for duplicate cleanup with full stats
        const stats = result.result || {};
        const found = stats.duplicates_found || 0;
        const deleted = stats.duplicates_deleted || 0;
        const totalAfter = stats.total_songs_after_cleanup || 0;
        const scanned = stats.total_songs_analyzed || 0;
        const passes = stats.passes_run || 1;
        
        if (result.success) {
          if (stats.dry_run) {
            // This shouldn't happen since we pass dry_run=false, but just in case
            addNotification('warning', 
              `⚠️ Καθαρισμός Διπλότυπων (Preview)\n` +
              `   • Σαρώθηκαν: ${scanned.toLocaleString()} τραγούδια\n` +
              `   • Βρέθηκαν: ${found} διπλότυπα\n` +
              `   (Δεν διαγράφηκαν - ήταν dry run)`
            );
          } else {
            addNotification('success', 
              `✅ Καθαρισμός Διπλότυπων Ολοκληρώθηκε!\n` +
              `   • Διαγράφηκαν: ${deleted} διπλότυπα\n` +
              `   • Περάσματα: ${passes}\n` +
              `   • Τραγούδια μετά: ${totalAfter.toLocaleString()}`
            );
          }
        } else {
          addNotification('error', `❌ Σφάλμα: ${result.error || 'Άγνωστο σφάλμα'}`);
        }
      }
      
      // Refresh status
      fetchSchedulerStatus();
    } catch (err) {
      console.error('Run job error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  const stopEnrichment = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/stream-monitor/enrich-release-years/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchEnrichmentStatus();
      }
    } catch (err) {
      console.error('Stop enrichment error:', err);
    }
  };

  // Recalculate Early Drop & LSI for all songs with listeners_samples
  const recalculateMetrics = async () => {
    setRunningJob('metrics');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/recalculate-metrics?dry_run=false`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        const stats = result.result || {};
        const total = stats.total_songs || 0;
        const updated = stats.updated || 0;
        const skipped = stats.skipped || 0;
        const errors = stats.errors || 0;
        const withEarlyDrop = stats.samples_stats?.with_early_drop || 0;
        const withLSI = stats.samples_stats?.with_lsi || 0;
        const noData = stats.samples_stats?.no_data || 0;
        
        addNotification('success', 
          `✅ Metrics (Νέα) Ολοκληρώθηκε!\n` +
          `   • Σύνολο τραγουδιών: ${total.toLocaleString()}\n` +
          `   • Ενημερώθηκαν: ${updated.toLocaleString()}\n` +
          `   • Με Early Drop: ${withEarlyDrop.toLocaleString()}\n` +
          `   • Με LSI: ${withLSI.toLocaleString()}\n` +
          `   • Χωρίς δεδομένα: ${noData}\n` +
          `   • Σφάλματα: ${errors}`
        );
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error}`);
      }
    } catch (err) {
      console.error('Recalculate metrics error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Recalculate metrics for LEGACY songs (those without listeners_samples array)
  const recalculateLegacyMetrics = async () => {
    setRunningJob('legacy-metrics');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/recalculate-legacy-metrics?dry_run=false`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        const stats = result.result || {};
        const total = stats.total_songs || 0;
        const updated = stats.updated || 0;
        const skipped = stats.skipped || 0;
        const errors = stats.errors || 0;
        
        addNotification('success', 
          `✅ Metrics (Παλιά) Ολοκληρώθηκε!\n` +
          `   • Σύνολο παλιών τραγουδιών: ${total.toLocaleString()}\n` +
          `   • Ενημερώθηκαν με Early Drop: ${updated.toLocaleString()}\n` +
          `   • Παραλείφθηκαν: ${skipped}\n` +
          `   • Σφάλματα: ${errors}\n` +
          `   (LSI δεν υπολογίστηκε - χρειάζεται 3+ samples)`
        );
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error}`);
      }
    } catch (err) {
      console.error('Recalculate legacy metrics error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Propagate release years to all matching records
  const propagateReleaseYears = async () => {
    setRunningJob('propagate-years');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/propagate-release-years?dry_run=false`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        const stats = result.result || {};
        const totalEnriched = stats.total_enriched_songs || 0;
        const songsPropagated = stats.songs_propagated || 0;
        const recordsUpdated = stats.records_updated || 0;
        
        addNotification('success', 
          `✅ Propagation Χρονιών Ολοκληρώθηκε!\n` +
          `   • Τραγούδια με χρονιά: ${totalEnriched.toLocaleString()}\n` +
          `   • Τραγούδια με propagation: ${songsPropagated.toLocaleString()}\n` +
          `   • Εγγραφές ενημερώθηκαν: ${recordsUpdated.toLocaleString()}`
        );
        
        // Refresh maintenance stats
        fetchSchedulerStatus();
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error}`);
      }
    } catch (err) {
      console.error('Propagate release years error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Cleanup HTML entities in song data
  const cleanupHtmlEntities = async () => {
    setRunningJob('cleanup-html');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/cleanup-html-entities?dry_run=false`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        const stats = result.result || {};
        addNotification('success', 
          `✅ Καθαρισμός HTML Entities Ολοκληρώθηκε!\n` +
          `   • Εγγραφές καθαρίστηκαν: ${(stats.cleaned || 0).toLocaleString()}\n` +
          `   • Fingerprints: ${(stats.fingerprints_cleaned || 0).toLocaleString()}`
        );
        fetchSchedulerStatus();
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error || result.result?.message}`);
      }
    } catch (err) {
      console.error('Cleanup HTML entities error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Backfill estimated metrics for old records
  const backfillEstimatedMetrics = async () => {
    setRunningJob('backfill-metrics');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/backfill-estimated-metrics?dry_run=false&batch_size=2000`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        const stats = result.result || {};
        addNotification('success', 
          `✅ Backfill Metrics Ολοκληρώθηκε!\n` +
          `   • Σύνολο εγγραφών: ${(stats.total_records || 0).toLocaleString()}\n` +
          `   • Ενημερώθηκαν: ${(stats.updated || 0).toLocaleString()}`
        );
        fetchSchedulerStatus();
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error || result.result?.message}`);
      }
    } catch (err) {
      console.error('Backfill metrics error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Refresh wrong release years with Spotify lookup
  const refreshWrongYears = async () => {
    setRunningJob('refresh-years');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/refresh-wrong-years?dry_run=false&suspicious_years_after=2023&batch_size=100`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        const stats = result.result || {};
        addNotification('success', 
          `✅ Refresh Ετών Ολοκληρώθηκε!\n` +
          `   • Ελέγχθηκαν: ${(stats.songs_checked || 0).toLocaleString()}\n` +
          `   • Διορθώθηκαν: ${(stats.songs_corrected || 0).toLocaleString()}`
        );
        fetchSchedulerStatus();
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error || result.result?.message}`);
      }
    } catch (err) {
      console.error('Refresh wrong years error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Cleanup out-of-schedule records
  const cleanupOutOfSchedule = async () => {
    setRunningJob('cleanup-schedule');
    try {
      const token = getToken();
      // First do a dry run to see what will be deleted
      const previewResponse = await fetch(`${BACKEND_URL}/api/admin/system/cleanup-out-of-schedule?dry_run=true&days_back=7`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const preview = await previewResponse.json();
      const totalFound = preview.total_found || 0;
      
      if (totalFound === 0) {
        addNotification('success', '✅ Δεν βρέθηκαν εγγραφές εκτός ωραρίου!');
        return;
      }
      
      // Ask for confirmation
      if (!window.confirm(`Βρέθηκαν ${totalFound} εγγραφές εκτός ωραρίου.\n\nΘέλετε να τις διαγράψετε;`)) {
        addNotification('info', 'ℹ️ Ακυρώθηκε η διαγραφή');
        return;
      }
      
      // Now do the actual delete
      const response = await fetch(`${BACKEND_URL}/api/admin/system/cleanup-out-of-schedule?dry_run=false&days_back=7`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      addNotification('success', 
        `✅ Καθαρισμός Εκτός Ωραρίου Ολοκληρώθηκε!\n` +
        `   • Διαγράφηκαν: ${(result.total_deleted || 0).toLocaleString()} εγγραφές`
      );
      fetchSchedulerStatus();
    } catch (err) {
      console.error('Cleanup out-of-schedule error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  // Run enrichment job manually
  const runEnrichment = async () => {
    setRunningJob('enrichment');
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/run-enrichment-now`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        addNotification('success', '✅ Ξεκίνησε η ενημέρωση ετών κυκλοφορίας!');
        // Start polling for enrichment status
        setEnrichmentPolling(true);
        fetchEnrichmentStatus();
      } else {
        addNotification('error', `❌ Σφάλμα: ${result.error || result.message}`);
      }
    } catch (err) {
      console.error('Run enrichment error:', err);
      addNotification('error', `❌ Σφάλμα: ${err.message}`);
    } finally {
      setRunningJob(null);
    }
  };

  const formatNextRun = (nextRun) => {
    if (!nextRun || nextRun === 'Not scheduled') return 'Not scheduled';
    try {
      const date = new Date(nextRun);
      const now = new Date();
      const diff = date - now;
      
      if (diff < 0) return 'Running now...';
      if (diff < 60000) return 'In < 1 min';
      if (diff < 3600000) return `In ${Math.round(diff / 60000)} min`;
      if (diff < 86400000) return `In ${Math.round(diff / 3600000)} hours`;
      
      return date.toLocaleString('el-GR');
    } catch {
      return nextRun;
    }
  };

  const getJobIcon = (jobId) => {
    if (jobId.includes('enrichment')) return '📚';
    if (jobId.includes('cleanup') || jobId.includes('dedup')) return '🧹';
    if (jobId.includes('email') || jobId.includes('drip')) return '📧';
    if (jobId.includes('jingle')) return '🎵';
    if (jobId.includes('backup')) return '💾';
    if (jobId.includes('memory')) return '🧠';
    if (jobId.includes('export')) return '📤';
    return '⏰';
  };

  const getJobCategory = (jobId) => {
    if (jobId.includes('enrichment') || jobId.includes('release')) return 'data';
    if (jobId.includes('cleanup') || jobId.includes('dedup') || jobId.includes('jingle')) return 'maintenance';
    if (jobId.includes('email') || jobId.includes('drip')) return 'marketing';
    if (jobId.includes('backup') || jobId.includes('memory')) return 'system';
    return 'other';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading scheduler status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-red-600 dark:text-red-400">❌ Error: {error}</p>
        <button 
          onClick={fetchSchedulerStatus}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Group jobs by category
  const jobsByCategory = schedulerData?.jobs?.reduce((acc, job) => {
    const category = getJobCategory(job.id);
    if (!acc[category]) acc[category] = [];
    acc[category].push(job);
    return acc;
  }, {}) || {};

  const categoryLabels = {
    data: '📊 Data Enrichment',
    maintenance: '🧹 Maintenance',
    marketing: '📧 Marketing',
    system: '💻 System',
    other: '⏰ Other'
  };

  return (
    <div className="space-y-6">
      {/* Persistent Notifications */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map(notification => (
            <div 
              key={notification.id}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                notification.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                  : notification.type === 'warning'
                  ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
              }`}
            >
              <div className="flex-1">
                <p className="font-medium">{notification.message}</p>
                <p className="text-xs opacity-70 mt-1">
                  {notification.timestamp.toLocaleTimeString('el-GR')}
                </p>
              </div>
              <button
                onClick={() => removeNotification(notification.id)}
                className="ml-4 p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full transition-colors"
                title="Κλείσιμο"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header with status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            ⏱️ Scheduled Jobs
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {schedulerData?.scheduler_running 
              ? `✅ Scheduler running with ${schedulerData?.total_jobs || 0} jobs`
              : '❌ Scheduler not running'}
          </p>
        </div>
        <div className="text-right flex items-center gap-3">
          {/* Browser Notifications Toggle */}
          <button
            onClick={toggleBrowserNotifications}
            className={`flex items-center gap-1 px-3 py-1 rounded text-sm ${
              notificationPermission === 'granted'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : notificationPermission === 'denied'
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            title={
              notificationPermission === 'granted' 
                ? 'Browser notifications ενεργές' 
                : notificationPermission === 'denied'
                ? 'Browser notifications blocked'
                : 'Ενεργοποίηση browser notifications'
            }
          >
            {notificationPermission === 'granted' ? (
              <Bell className="w-4 h-4" />
            ) : (
              <BellOff className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {notificationPermission === 'granted' ? 'On' : 'Off'}
            </span>
          </button>
          
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last refresh: {lastRefresh?.toLocaleTimeString('el-GR')}
            </p>
            <button 
              onClick={() => { fetchSchedulerStatus(); fetchEnrichmentStatus(); }}
              className="mt-1 px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Content Generation Failed Sections Alert */}
      {contentStatus?.failed_sections?.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-bold text-red-800 dark:text-red-200 text-lg mb-2">
                Αποτυχημένες Ενότητες Περιεχομένου
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                Ημερομηνία: {contentStatus.last_generation || 'Άγνωστη'} • 
                Επιτυχείς: {contentStatus.success_count || 0} • 
                Αποτυχίες: {contentStatus.failed_count || contentStatus.failed_sections.length}
              </p>
              <div className="space-y-2">
                {contentStatus.failed_sections.map((section, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-2 bg-red-100 dark:bg-red-900/40 px-3 py-2 rounded"
                  >
                    <span className="text-red-600 dark:text-red-400">❌</span>
                    <span className="font-semibold text-red-800 dark:text-red-200">
                      {typeof section === 'string' ? section : section.section}
                    </span>
                    {section.error && (
                      <span className="text-xs text-red-600 dark:text-red-400 ml-2">
                        ({section.error})
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-red-600 dark:text-red-400">
                💡 Δοκιμάστε να τρέξετε χειροκίνητα το &quot;📝 Content Generation&quot; παρακάτω
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Content Status Summary */}
      {contentStatus && !contentStatus.failed_sections?.length && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">✅</span>
            <div>
              <span className="font-medium text-green-800 dark:text-green-200">
                Content Generation: {contentStatus.status === 'up_to_date' ? 'Ενημερωμένο' : contentStatus.status}
              </span>
              <span className="text-sm text-green-600 dark:text-green-400 ml-2">
                ({contentStatus.total_sections} sections • {contentStatus.last_generation})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Enrichment Progress Banner - SAME as StreamMonitor */}
      {enrichmentStatus && enrichmentStatus.status === 'running' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="animate-spin">🔄</span>
              <span className="font-medium text-blue-700 dark:text-blue-300">
                Ενημέρωση Ετών Κυκλοφορίας σε εξέλιξη...
              </span>
            </div>
            <button
              onClick={stopEnrichment}
              className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Διακοπή
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2.5 mb-2 overflow-hidden">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${enrichmentStatus.total > 0 ? Math.min(100, Math.round((enrichmentStatus.processed / enrichmentStatus.total) * 100)) : 0}%` }}
            />
          </div>
          
          <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
            <span>
              {enrichmentStatus.processed || 0} / {enrichmentStatus.total || 0} τραγούδια
              {enrichmentStatus.processed > enrichmentStatus.total && (
                <span className="text-xs text-orange-500 ml-1">(+{enrichmentStatus.processed - enrichmentStatus.total} νέα)</span>
              )}
            </span>
            <span>
              {enrichmentStatus.total > 0 
                ? `${Math.min(100, Math.round((enrichmentStatus.processed / enrichmentStatus.total) * 100))}%`
                : '0%'
              }
            </span>
          </div>
          
          {/* Show updated/failed counts */}
          <div className="flex justify-between text-xs mt-1">
            <span className="text-green-600 dark:text-green-400">
              ✅ Βρέθηκαν: {enrichmentStatus.updated || 0} έτη
            </span>
            <span className="text-red-500 dark:text-red-400">
              ❌ Δεν βρέθηκαν: {enrichmentStatus.failed || 0}
            </span>
          </div>
          
          {enrichmentStatus.current_song && (
            <p className="mt-2 text-xs text-blue-500 dark:text-blue-400 truncate">
              📀 {enrichmentStatus.current_song}
            </p>
          )}
        </div>
      )}

      {/* Quick Stats */}
      {enrichmentStatus && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Τραγούδια χωρίς Έτος</h3>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {enrichmentStatus.total?.toLocaleString() || 0}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Κατάσταση</h3>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">
              {enrichmentStatus.status === 'idle' ? '✅ Αδρανές' : 
               enrichmentStatus.status === 'running' ? '🔄 Τρέχει' :
               enrichmentStatus.status === 'complete' ? '✅ Ολοκληρώθηκε' :
               enrichmentStatus.status === 'stopped' ? '⏹️ Σταμάτησε' :
               enrichmentStatus.status === 'error' ? '❌ Σφάλμα' : enrichmentStatus.status}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800 dark:text-purple-200">Προγραμματισμένα Jobs</h3>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {schedulerData?.total_jobs || 0}
            </p>
          </div>
        </div>
      )}

      {/* Manual Triggers */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">⚙️ Αυτοματισμοί Stream Monitor</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Hourly Jobs */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">⏰ Κάθε Ώρα</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>:00 - Ενημέρωση ετών (νέα τραγούδια)</li>
              <li>:15 - Καθαρισμός jingles/station IDs</li>
              <li>:20 - Καθαρισμός HTML entities</li>
              <li>:45 - Καθαρισμός διπλότυπων</li>
              <li>:50 - Διάδοση ετών σε παραλλαγές</li>
            </ul>
          </div>
          
          {/* Nightly Jobs */}
          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
            <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">🌙 Νυχτερινά (Deep Jobs)</h4>
            <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-1">
              <li>03:30 - Backfill estimated metrics</li>
              <li>04:00 - Deep καθαρισμός jingles</li>
              <li>04:30 - Deep καθαρισμός διπλότυπων</li>
              <li>05:00 - Deep ενημέρωση ετών (500 τραγούδια)</li>
            </ul>
          </div>
        </div>
        
        {/* Weekly Jobs */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg mb-4">
          <h4 className="font-medium text-emerald-800 dark:text-emerald-200 mb-2">📅 Εβδομαδιαία</h4>
          <ul className="text-sm text-emerald-700 dark:text-emerald-300">
            <li>Κυριακή 06:00 - Διόρθωση λάθος ετών με Spotify (έτη ≥2023)</li>
          </ul>
        </div>
        
        {/* Health Check */}
        <div className="bg-rose-50 dark:bg-rose-900/20 p-3 rounded-lg">
          <h4 className="font-medium text-rose-800 dark:text-rose-200 mb-2">🏥 Health Check (23:00)</h4>
          <ul className="text-sm text-rose-700 dark:text-rose-300 space-y-1">
            <li>• Database: Σύνδεση και μετρητές</li>
            <li>• Content: Ετοιμότητα για αύριο</li>
            <li>• Stream Monitor: Πρόσφατα plays</li>
            <li>• Enrichment: Ποσοστό ετών</li>
            <li>• Data Quality: HTML entities</li>
            <li>• External APIs: Spotify, MusicBrainz</li>
          </ul>
          <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
            Τρέχει 1 ώρα πριν το content generation (00:01)
          </p>
        </div>
        
        {/* Fingerprint Settings */}
        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">🎤 Audio Fingerprinting</h4>
          <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
            <li>• Trigger: Μετά από 5 λεπτά ίδιου metadata</li>
            <li>• Cooldown: 5 λεπτά μεταξύ προσπαθειών</li>
            <li>• Ημερήσιο limit: 20 προσπάθειες/σταθμό</li>
            <li>• Λειτουργεί για: Live Broadcast, Unknown, κλπ</li>
          </ul>
        </div>
        
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          ✅ Όλα τα jobs τρέχουν αυτόματα στο background. Δεν απαιτείται χειροκίνητη παρέμβαση.
        </p>
      </div>

      {/* Manual Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">🔧 Χειροκίνητες Ενέργειες</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Cleanup Out-of-Schedule */}
          <button
            onClick={cleanupOutOfSchedule}
            disabled={runningJob === 'cleanup-schedule'}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              runningJob === 'cleanup-schedule'
                ? 'bg-orange-200 dark:bg-orange-900/50 text-orange-600 cursor-wait'
                : 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/40'
            }`}
          >
            {runningJob === 'cleanup-schedule' ? (
              <>
                <span className="animate-spin">⏳</span>
                Καθαρισμός...
              </>
            ) : (
              <>
                🕐 Καθαρισμός Εκτός Ωραρίου
              </>
            )}
          </button>

          {/* Backfill Metrics */}
          <button
            onClick={backfillEstimatedMetrics}
            disabled={runningJob === 'backfill-metrics'}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              runningJob === 'backfill-metrics'
                ? 'bg-blue-200 dark:bg-blue-900/50 text-blue-600 cursor-wait'
                : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/40'
            }`}
          >
            {runningJob === 'backfill-metrics' ? (
              <>
                <span className="animate-spin">⏳</span>
                Υπολογισμός...
              </>
            ) : (
              <>
                📊 Backfill Metrics
              </>
            )}
          </button>

          {/* Refresh Wrong Years */}
          <button
            onClick={refreshWrongYears}
            disabled={runningJob === 'refresh-years'}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              runningJob === 'refresh-years'
                ? 'bg-purple-200 dark:bg-purple-900/50 text-purple-600 cursor-wait'
                : 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/40'
            }`}
          >
            {runningJob === 'refresh-years' ? (
              <>
                <span className="animate-spin">⏳</span>
                Έλεγχος...
              </>
            ) : (
              <>
                📅 Διόρθωση Ετών (Spotify)
              </>
            )}
          </button>

          {/* Run Enrichment */}
          <button
            onClick={runEnrichment}
            disabled={runningJob === 'enrichment' || enrichmentStatus?.status === 'running'}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              runningJob === 'enrichment' || enrichmentStatus?.status === 'running'
                ? 'bg-green-200 dark:bg-green-900/50 text-green-600 cursor-wait'
                : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/40'
            }`}
          >
            {runningJob === 'enrichment' || enrichmentStatus?.status === 'running' ? (
              <>
                <span className="animate-spin">⏳</span>
                Ενημέρωση...
              </>
            ) : (
              <>
                📚 Ενημέρωση Ετών
              </>
            )}
          </button>
        </div>
        
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          💡 Αυτές οι ενέργειες τρέχουν κανονικά αυτόματα, αλλά μπορείτε να τις ενεργοποιήσετε χειροκίνητα.
        </p>
      </div>

      {/* Jobs by Category */}
      {Object.entries(jobsByCategory).map(([category, jobs]) => (
        <div key={category} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              {categoryLabels[category] || category}
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {jobs.map((job) => (
              <div 
                key={job.id} 
                className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{getJobIcon(job.id)}</span>
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white text-sm">
                      {job.name || job.id}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {job.trigger}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${
                    job.pending ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    {job.pending ? '⏳ Pending' : '✅ Active'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Next: {formatNextRun(job.next_run)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SchedulerStatus;
